import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

type AdminUser = {
  username: string
  role: string
  name: string
}

type AdminLoginResponse = {
  authenticated: boolean
  user: {
    username: string
    role: string
  }
  expires_at: string
}

type AdminSessionResponse = {
  authenticated: boolean
  user: {
    username: string
    role: string
  }
  expires_at: string
}

type AdminLogoutResponse = {
  success: boolean
}

export type AdminSeriesPoint = {
  bucket: string
  bucket_start: string
  bucket_end: string
  active_users: number
  average_items_tracked_per_active_user: number
  total_active_tracked_items: number
}

export type AdminSeriesResponse = {
  period: string
  bucket: string
  window_start: string
  window_end: string
  points: AdminSeriesPoint[]
}

export type AdminSummaryResponse = {
  period: string
  window_start: string
  window_end: string
  active_users: number
  average_items_tracked_per_active_user: number
  total_active_tracked_items: number
}

export type AdminCarrierItemRow = {
  carrier_id: number
  carrier_name: string
  total_items: number
  active_items: number
  delivered_stopped_items: number
  stale_items: number
}

export type AdminItemsPerCarrierResponse = {
  period: string
  window_start: string
  window_end: string
  totals: {
    total_items: number
    active_items: number
    delivered_stopped_items: number
    stale_items: number
  }
  items_per_carrier: AdminCarrierItemRow[]
}

export type AdminSeriesPerCarrierPoint = {
  bucket: string
  bucket_start: string
  bucket_end: string
  total_items: number
  active_items: number
  delivered_stopped_items: number
  stale_items: number
}

export type AdminSeriesPerCarrierRow = {
  carrier_id: number
  carrier_name: string
  points: AdminSeriesPerCarrierPoint[]
}

export type AdminSeriesPerCarrierResponse = {
  period: string
  bucket: string
  window_start: string
  window_end: string
  totals: {
    total_items: number
    active_items: number
    delivered_stopped_items: number
    stale_items: number
  }
  carriers: AdminSeriesPerCarrierRow[]
}

export type AdminPlatform = string

export type AdminPlatformSummaryRow = {
  platform: AdminPlatform
  total_items: number
  active_items: number
  delivered_stopped_items: number
  stale_items: number
  active_users: number
  average_items_tracked_per_active_user: number
}

export type AdminSummaryByPlatformResponse = {
  period: string
  window_start: string
  window_end: string
  totals: {
    total_items: number
    active_items: number
    delivered_stopped_items: number
    stale_items: number
    active_users: number
    average_items_tracked_per_active_user: number
  }
  platforms: AdminPlatformSummaryRow[]
}

export type AdminSeriesByPlatformPoint = {
  bucket: string
  bucket_start: string
  bucket_end: string
  total_items: number
  active_items: number
  delivered_stopped_items: number
  stale_items: number
  active_users: number
  average_items_tracked_per_active_user: number
}

export type AdminSeriesByPlatformRow = {
  platform: AdminPlatform
  points: AdminSeriesByPlatformPoint[]
}

export type AdminSeriesByPlatformResponse = {
  period: string
  bucket: string
  window_start: string
  window_end: string
  totals: {
    total_items: number
    active_items: number
    delivered_stopped_items: number
    stale_items: number
    active_users: number
    average_items_tracked_per_active_user: number
  }
  platforms: AdminSeriesByPlatformRow[]
}

export type AdminFailureReasonCount = {
  reason: string
  count: number
}

export type AdminNotificationTypeBreakdownRow = {
  notification_type: string
  total: number
  sent: number
  failed: number
  suppressed: number
  pending: number
}

export type AdminNotificationsReportResponse = {
  period: string
  window_start: string
  window_end: string
  total: number
  sent: number
  failed: number
  suppressed: number
  pending: number
  by_type: AdminNotificationTypeBreakdownRow[]
  top_failure_reasons: AdminFailureReasonCount[]
}

export type AdminNotificationIntegrityStatusRow = {
  delivery_status: string
  count: number
}

export type AdminNotificationIntegrityReportResponse = {
  period: string
  window_start: string
  window_end: string
  total_rows: number
  valid_rows: number
  invalid_rows: number
  invalid_carrier_rows: number
  invalid_fcm_token_rows: number
  failed_invalid_rows: number
  invalid_by_status: AdminNotificationIntegrityStatusRow[]
}

export type AdminBrandfetchMetrics = {
  brandfetch_calls_total: number
  brandfetch_brand_calls_total: number
  brandfetch_search_calls_total: number
  brandfetch_cache_hits_total: number
  brandfetch_updates_total: number
  cache_hit_rate: number
  update_success_rate: number
  total_carriers: number
  carriers_with_colors: number
  carriers_missing_colors: number
  coverage_rate: number
  domain_mappings_total: number
  domain_mapped_with_colors: number
  domain_mapped_missing_colors: number
  domain_mapped_coverage_rate: number
  last_color_fetched_at: string | null
  brand_api_calls_current_month: number
  brand_api_limit_monthly: number
  brand_api_remaining_monthly: number
  brand_api_percent_used: number
  search_api_calls_current_month: number
  search_api_limit_monthly: number
  search_api_remaining_monthly: number
  search_api_percent_used: number
}

export type AdminBrandfetchMetricsResponse = {
  metrics: AdminBrandfetchMetrics
}

export type AdminBrandfetchColoredCarrier = {
  carrier_code: number
  carrier_name: string
  carrier_bg: string
  carrier_txt: string
  color_fetched_at: string | null
}

export type AdminBrandfetchReportResponse = {
  total_carriers: number
  carriers_with_colors: number
  carriers_missing_colors: number
  domain_mappings_total: number
  domain_mapped_with_colors: number
  domain_mapped_missing_colors: number
  domain_mapped_missing: string[]
  colored_carriers: AdminBrandfetchColoredCarrier[]
  brandfetch_calls_total: number
  brandfetch_cache_hits_total: number
  brandfetch_updates_total: number
}

export type AdminDashboardTopCarrierRow = {
  carrier_id: number
  carrier_name: string
  active_items: number
  delivered_stopped_items: number
  stale_items: number
  total_items: number
}

export type AdminDashboardNotificationSummary = {
  total: number
  sent: number
  failed: number
  suppressed: number
  pending: number
  top_failure_reasons: AdminFailureReasonCount[]
}

export type AdminDashboardDeviceInsights = {
  active_users: number
  average_active_span_days: number
  median_active_span_days: number
  p90_active_span_days: number
  users_with_registered_devices: number
  users_with_multiple_devices: number
  multi_device_user_rate: number
  average_active_devices_per_user: number
  max_active_devices_per_user: number
}

export type AdminDashboardDeviceInsightsByPlatformRow = {
  platform: AdminPlatform
  active_users: number
  average_active_span_days: number
  median_active_span_days: number
  p90_active_span_days: number
  users_with_registered_devices: number
  users_with_multiple_devices: number
  multi_device_user_rate: number
  average_active_devices_per_user: number
  max_active_devices_per_user: number
}

export type AdminDashboardReportResponse = {
  period: string
  window_start: string
  window_end: string
  tracking_summary: AdminSummaryResponse
  carrier_totals: {
    active_items: number
    delivered_stopped_items: number
    stale_items: number
    total_items: number
  }
  top_carriers: AdminDashboardTopCarrierRow[]
  platform_totals: {
    active_users: number
    active_items: number
    average_items_tracked_per_active_user: number
    delivered_stopped_items: number
    stale_items: number
    total_items: number
  }
  platforms: AdminPlatformSummaryRow[]
  user_activity_device_insights: AdminDashboardDeviceInsights
  user_activity_device_insights_by_platform: AdminDashboardDeviceInsightsByPlatformRow[]
  notification_summary: AdminDashboardNotificationSummary
  brandfetch_summary: AdminBrandfetchMetrics
}

type AdminDashboardReportApiResponse = Omit<
  AdminDashboardReportResponse,
  'top_carriers' | 'platforms' | 'user_activity_device_insights' | 'user_activity_device_insights_by_platform' | 'notification_summary'
> & {
  top_carriers?: AdminDashboardTopCarrierRow[]
  platforms?: AdminPlatformSummaryRow[]
  user_activity_device_insights?: AdminDashboardDeviceInsights
  user_activity_device_insights_by_platform?: AdminDashboardDeviceInsightsByPlatformRow[]
  notification_summary: Omit<AdminDashboardNotificationSummary, 'top_failure_reasons'> & {
    top_failure_reasons?: AdminFailureReasonCount[]
  }
}

export const useAdminStore = defineStore('admin', () => {
  const user = ref<AdminUser | null>(null)
  const expiresAt = ref<string | null>(null)
  const authError = ref('')
  const hasCheckedSession = ref(false)
  const selectedPeriod = ref<'24h' | '7d' | '30d' | '90d' | 'all'>('30d')
  const selectedBucket = ref<'day' | 'week' | 'month'>('day')
  let sessionRequestInFlight: Promise<boolean> | null = null
  const isAuthenticated = computed(() => !!user.value)

  function joinApiUrl(baseUrl: string, path: string): string {
    const normalizedBase = baseUrl.replace(/\/+$/, '')
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    return `${normalizedBase}${normalizedPath}`
  }

  function resolveApiBase(configuredBase: string, fallbackBase: string): string {
    const trimmed = configuredBase.trim()

    if (!trimmed) {
      return fallbackBase
    }

    // In local development, prefer same-origin proxy paths to avoid CORS issues.
    if (import.meta.env.DEV) {
      try {
        const url = new URL(trimmed)
        return url.pathname || fallbackBase
      } catch {
        return trimmed
      }
    }

    return trimmed
  }

  function setAuthenticatedUser(username: string, role: string, expires: string) {
    user.value = {
      username,
      role,
      name: username,
    }
    expiresAt.value = expires
    authError.value = ''
  }

  function clearAuthState() {
    user.value = null
    expiresAt.value = null
  }

  async function login(username: string, password: string): Promise<boolean> {
    authError.value = ''

    if (!username || !password) {
      authError.value = 'Username and password are required.'
      return false
    }

    const configuredBase = import.meta.env.VITE_ADMIN_AUTH_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    if (!baseUrl) {
      authError.value = 'Missing VITE_ADMIN_AUTH_BASE_URL'
      return false
    }

    try {
      const response = await fetch(joinApiUrl(baseUrl, '/auth/login'), {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      })

      if (!response.ok) {
        authError.value = `Login failed (${response.status}).`
        return false
      }

      const data = await response.json() as AdminLoginResponse
      if (!data.authenticated) {
        authError.value = 'Invalid username or password.'
        return false
      }

      setAuthenticatedUser(data.user.username, data.user.role, data.expires_at)
      hasCheckedSession.value = true
      return true
    } catch {
      authError.value = 'Unable to reach auth service. Please try again.'
      return false
    }
  }

  async function ensureSession(force = false): Promise<boolean> {
    if (!force && hasCheckedSession.value) {
      return isAuthenticated.value
    }

    if (sessionRequestInFlight) {
      return await sessionRequestInFlight
    }

    const configuredBase = import.meta.env.VITE_ADMIN_AUTH_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    if (!baseUrl) {
      authError.value = 'Missing VITE_ADMIN_AUTH_BASE_URL'
      hasCheckedSession.value = true
      clearAuthState()
      return false
    }

    sessionRequestInFlight = (async () => {
      try {
        const response = await fetch(joinApiUrl(baseUrl, '/auth/session'), {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
          credentials: 'include',
        })

        if (!response.ok) {
          clearAuthState()
          return false
        }

        const data = await response.json() as AdminSessionResponse
        if (!data.authenticated) {
          clearAuthState()
          return false
        }

        setAuthenticatedUser(data.user.username, data.user.role, data.expires_at)
        return true
      } catch {
        clearAuthState()
        return false
      } finally {
        hasCheckedSession.value = true
        sessionRequestInFlight = null
      }
    })()

    return await sessionRequestInFlight
  }

  async function logout() {
    const configuredBase = import.meta.env.VITE_ADMIN_AUTH_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')

    try {
      if (baseUrl) {
        const response = await fetch(joinApiUrl(baseUrl, '/auth/logout'), {
          method: 'POST',
          headers: {
            accept: 'application/json',
          },
          credentials: 'include',
          body: '',
        })

        if (response.ok) {
          const data = await response.json() as AdminLogoutResponse
          if (!data.success) {
            // Treat unsuccessful logout response as local logout only.
            console.warn('Admin logout endpoint returned success=false')
          }
        }
      }
    } catch {
      // Local state is still cleared below to avoid UI lock-in.
    } finally {
      clearAuthState()
      authError.value = ''
      hasCheckedSession.value = false
    }
  }

  function isLoggedIn(): boolean {
    return isAuthenticated.value
  }

  async function getUsageSeries(period = '30d', bucket = 'day'): Promise<AdminSeriesResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(
      baseUrl,
      `/admin/reports/series?period=${encodeURIComponent(period)}&bucket=${encodeURIComponent(bucket)}`,
    )

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin reports API error: ${response.status}`)
    }

    return await response.json() as AdminSeriesResponse
  }

  async function getUsageSummary(period = '30d'): Promise<AdminSummaryResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(baseUrl, `/admin/reports/summary?period=${encodeURIComponent(period)}`)

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin summary API error: ${response.status}`)
    }

    return await response.json() as AdminSummaryResponse
  }

  function getUsageSeriesDemo(): AdminSeriesResponse {
    return {
      period: '30d',
      bucket: 'day',
      window_start: '2026-03-14T00:00:00Z',
      window_end: '2026-04-13T00:00:00Z',
      points: [
        { bucket: 'day', bucket_start: '2026-03-14T00:00:00Z', bucket_end: '2026-03-15T00:00:00Z', active_users: 8, average_items_tracked_per_active_user: 2.25, total_active_tracked_items: 18 },
        { bucket: 'day', bucket_start: '2026-03-16T00:00:00Z', bucket_end: '2026-03-17T00:00:00Z', active_users: 10, average_items_tracked_per_active_user: 2.2, total_active_tracked_items: 22 },
        { bucket: 'day', bucket_start: '2026-03-18T00:00:00Z', bucket_end: '2026-03-19T00:00:00Z', active_users: 12, average_items_tracked_per_active_user: 2.33, total_active_tracked_items: 28 },
        { bucket: 'day', bucket_start: '2026-03-20T00:00:00Z', bucket_end: '2026-03-21T00:00:00Z', active_users: 15, average_items_tracked_per_active_user: 2.33, total_active_tracked_items: 35 },
        { bucket: 'day', bucket_start: '2026-03-22T00:00:00Z', bucket_end: '2026-03-23T00:00:00Z', active_users: 14, average_items_tracked_per_active_user: 2.29, total_active_tracked_items: 32 },
        { bucket: 'day', bucket_start: '2026-03-24T00:00:00Z', bucket_end: '2026-03-25T00:00:00Z', active_users: 18, average_items_tracked_per_active_user: 2.33, total_active_tracked_items: 42 },
        { bucket: 'day', bucket_start: '2026-03-26T00:00:00Z', bucket_end: '2026-03-27T00:00:00Z', active_users: 20, average_items_tracked_per_active_user: 2.4, total_active_tracked_items: 48 },
        { bucket: 'day', bucket_start: '2026-03-28T00:00:00Z', bucket_end: '2026-03-29T00:00:00Z', active_users: 22, average_items_tracked_per_active_user: 2.36, total_active_tracked_items: 52 },
        { bucket: 'day', bucket_start: '2026-03-30T00:00:00Z', bucket_end: '2026-03-31T00:00:00Z', active_users: 19, average_items_tracked_per_active_user: 2.37, total_active_tracked_items: 45 },
        { bucket: 'day', bucket_start: '2026-04-01T00:00:00Z', bucket_end: '2026-04-02T00:00:00Z', active_users: 24, average_items_tracked_per_active_user: 2.5, total_active_tracked_items: 60 },
        { bucket: 'day', bucket_start: '2026-04-03T00:00:00Z', bucket_end: '2026-04-04T00:00:00Z', active_users: 21, average_items_tracked_per_active_user: 2.38, total_active_tracked_items: 50 },
        { bucket: 'day', bucket_start: '2026-04-07T00:00:00Z', bucket_end: '2026-04-08T00:00:00Z', active_users: 18, average_items_tracked_per_active_user: 2.11, total_active_tracked_items: 38 },
        { bucket: 'day', bucket_start: '2026-04-09T00:00:00Z', bucket_end: '2026-04-10T00:00:00Z', active_users: 20, average_items_tracked_per_active_user: 2.2, total_active_tracked_items: 44 },
        { bucket: 'day', bucket_start: '2026-04-11T00:00:00Z', bucket_end: '2026-04-12T00:00:00Z', active_users: 22, average_items_tracked_per_active_user: 2.36, total_active_tracked_items: 52 },
        { bucket: 'day', bucket_start: '2026-04-13T00:00:00Z', bucket_end: '2026-04-14T00:00:00Z', active_users: 24, average_items_tracked_per_active_user: 2.5, total_active_tracked_items: 60 },
      ],
    }
  }

  function getUsageSummaryDemo(): AdminSummaryResponse {
    return {
      period: '30d',
      window_start: '2026-03-14T20:19:44.474796Z',
      window_end: '2026-04-13T20:19:44.474796Z',
      active_users: 1,
      average_items_tracked_per_active_user: 4,
      total_active_tracked_items: 4,
    }
  }

  async function getDashboardReport(period = '30d', topCarriersLimit = 10): Promise<AdminDashboardReportResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(
      baseUrl,
      `/admin/reports/dashboard?period=${encodeURIComponent(period)}&top_carriers_limit=${encodeURIComponent(topCarriersLimit)}`,
    )

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin dashboard report API error: ${response.status}`)
    }

    const payload = await response.json() as AdminDashboardReportApiResponse

    return {
      ...payload,
      top_carriers: payload.top_carriers ?? [],
      platforms: payload.platforms ?? [],
      user_activity_device_insights: payload.user_activity_device_insights ?? {
        active_users: 0,
        average_active_span_days: 0,
        median_active_span_days: 0,
        p90_active_span_days: 0,
        users_with_registered_devices: 0,
        users_with_multiple_devices: 0,
        multi_device_user_rate: 0,
        average_active_devices_per_user: 0,
        max_active_devices_per_user: 0,
      },
      user_activity_device_insights_by_platform: payload.user_activity_device_insights_by_platform ?? [],
      notification_summary: {
        ...payload.notification_summary,
        top_failure_reasons: payload.notification_summary.top_failure_reasons ?? [],
      },
    }
  }

  function getDashboardReportDemo(): AdminDashboardReportResponse {
    return {
      period: '30d',
      window_start: '2026-03-16T19:59:56.767829Z',
      window_end: '2026-04-15T19:59:56.767829Z',
      tracking_summary: {
        period: '30d',
        window_start: '2026-03-16T19:59:56.767829Z',
        window_end: '2026-04-15T19:59:56.767829Z',
        active_users: 1,
        average_items_tracked_per_active_user: 4,
        total_active_tracked_items: 4,
      },
      carrier_totals: {
        active_items: 0,
        delivered_stopped_items: 2,
        stale_items: 2,
        total_items: 4,
      },
      top_carriers: [
        {
          carrier_id: 100002,
          carrier_name: 'UPS',
          active_items: 0,
          delivered_stopped_items: 1,
          stale_items: 1,
          total_items: 2,
        },
        {
          carrier_id: 101008,
          carrier_name: 'BOX NOW',
          active_items: 0,
          delivered_stopped_items: 0,
          stale_items: 1,
          total_items: 1,
        },
        {
          carrier_id: 100003,
          carrier_name: 'FedEx',
          active_items: 0,
          delivered_stopped_items: 1,
          stale_items: 0,
          total_items: 1,
        },
      ],
      platform_totals: {
        active_users: 1,
        active_items: 0,
        average_items_tracked_per_active_user: 0,
        delivered_stopped_items: 2,
        stale_items: 2,
        total_items: 4,
      },
      platforms: [
        {
          platform: 'android',
          active_users: 1,
          active_items: 0,
          average_items_tracked_per_active_user: 0,
          delivered_stopped_items: 2,
          stale_items: 2,
          total_items: 4,
        },
        {
          platform: 'zeppos',
          active_users: 0,
          active_items: 0,
          average_items_tracked_per_active_user: 0,
          delivered_stopped_items: 0,
          stale_items: 0,
          total_items: 0,
        },
      ],
      user_activity_device_insights: {
        active_users: 1,
        average_active_span_days: 8,
        median_active_span_days: 8,
        p90_active_span_days: 8,
        users_with_registered_devices: 1,
        users_with_multiple_devices: 0,
        multi_device_user_rate: 0,
        average_active_devices_per_user: 1,
        max_active_devices_per_user: 1,
      },
      user_activity_device_insights_by_platform: [
        {
          platform: 'android',
          active_users: 1,
          average_active_span_days: 8,
          median_active_span_days: 8,
          p90_active_span_days: 8,
          users_with_registered_devices: 1,
          users_with_multiple_devices: 0,
          multi_device_user_rate: 0,
          average_active_devices_per_user: 1,
          max_active_devices_per_user: 1,
        },
      ],
      notification_summary: {
        total: 38,
        sent: 35,
        failed: 3,
        suppressed: 0,
        pending: 0,
        top_failure_reasons: [
          {
            reason: 'no_device',
            count: 3,
          },
        ],
      },
      brandfetch_summary: {
        brandfetch_calls_total: 0,
        brandfetch_brand_calls_total: 0,
        brandfetch_search_calls_total: 0,
        brandfetch_cache_hits_total: 0,
        brandfetch_updates_total: 0,
        cache_hit_rate: 0,
        update_success_rate: 0,
        total_carriers: 3259,
        carriers_with_colors: 66,
        carriers_missing_colors: 3193,
        coverage_rate: 0.020251610923596196,
        domain_mappings_total: 295,
        domain_mapped_with_colors: 64,
        domain_mapped_missing_colors: 184,
        domain_mapped_coverage_rate: 0.21694915254237288,
        last_color_fetched_at: '2026-04-15T15:43:45.750232-04:00',
        brand_api_calls_current_month: 0,
        brand_api_limit_monthly: 500,
        brand_api_remaining_monthly: 500,
        brand_api_percent_used: 0,
        search_api_calls_current_month: 0,
        search_api_limit_monthly: 500,
        search_api_remaining_monthly: 500,
        search_api_percent_used: 0,
      },
    }
  }

  async function getItemsPerCarrier(period = '30d'): Promise<AdminItemsPerCarrierResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(baseUrl, `/admin/reports/items_per_carrier?period=${encodeURIComponent(period)}`)

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin items-per-carrier API error: ${response.status}`)
    }

    return await response.json() as AdminItemsPerCarrierResponse
  }

  function getItemsPerCarrierDemo(): AdminItemsPerCarrierResponse {
    return {
      period: '30d',
      window_start: '2026-03-14T00:00:00Z',
      window_end: '2026-04-13T00:00:00Z',
      totals: {
        total_items: 174,
        active_items: 104,
        delivered_stopped_items: 46,
        stale_items: 24,
      },
      items_per_carrier: [
        { carrier_id: 100001, carrier_name: 'USPS', total_items: 58, active_items: 36, delivered_stopped_items: 14, stale_items: 8 },
        { carrier_id: 100003, carrier_name: 'UPS', total_items: 42, active_items: 28, delivered_stopped_items: 10, stale_items: 4 },
        { carrier_id: 100004, carrier_name: 'FedEx', total_items: 36, active_items: 24, delivered_stopped_items: 8, stale_items: 4 },
        { carrier_id: 100005, carrier_name: 'DHL', total_items: 22, active_items: 12, delivered_stopped_items: 8, stale_items: 2 },
        { carrier_id: 100002, carrier_name: 'Other', total_items: 16, active_items: 4, delivered_stopped_items: 6, stale_items: 6 },
      ],
    }
  }

  async function getSeriesPerCarrier(period = '30d', bucket = 'day'): Promise<AdminSeriesPerCarrierResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(
      baseUrl,
      `/admin/reports/series_per_carrier?period=${encodeURIComponent(period)}&bucket=${encodeURIComponent(bucket)}`,
    )

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin series-per-carrier API error: ${response.status}`)
    }

    return await response.json() as AdminSeriesPerCarrierResponse
  }

  function getSeriesPerCarrierDemo(): AdminSeriesPerCarrierResponse {
    return {
      period: '30d',
      bucket: 'day',
      window_start: '2026-03-14T00:00:00Z',
      window_end: '2026-04-13T00:00:00Z',
      totals: {
        total_items: 78,
        active_items: 52,
        delivered_stopped_items: 18,
        stale_items: 8,
      },
      carriers: [
        {
          carrier_id: 100003,
          carrier_name: 'UPS',
          points: [
            { bucket: 'day', bucket_start: '2026-04-10T00:00:00Z', bucket_end: '2026-04-11T00:00:00Z', total_items: 7, active_items: 5, delivered_stopped_items: 1, stale_items: 1 },
            { bucket: 'day', bucket_start: '2026-04-11T00:00:00Z', bucket_end: '2026-04-12T00:00:00Z', total_items: 8, active_items: 6, delivered_stopped_items: 1, stale_items: 1 },
            { bucket: 'day', bucket_start: '2026-04-12T00:00:00Z', bucket_end: '2026-04-13T00:00:00Z', total_items: 9, active_items: 6, delivered_stopped_items: 2, stale_items: 1 },
          ],
        },
        {
          carrier_id: 100004,
          carrier_name: 'FedEx',
          points: [
            { bucket: 'day', bucket_start: '2026-04-10T00:00:00Z', bucket_end: '2026-04-11T00:00:00Z', total_items: 6, active_items: 4, delivered_stopped_items: 1, stale_items: 1 },
            { bucket: 'day', bucket_start: '2026-04-11T00:00:00Z', bucket_end: '2026-04-12T00:00:00Z', total_items: 7, active_items: 5, delivered_stopped_items: 1, stale_items: 1 },
            { bucket: 'day', bucket_start: '2026-04-12T00:00:00Z', bucket_end: '2026-04-13T00:00:00Z', total_items: 8, active_items: 5, delivered_stopped_items: 2, stale_items: 1 },
          ],
        },
        {
          carrier_id: 100001,
          carrier_name: 'USPS',
          points: [
            { bucket: 'day', bucket_start: '2026-04-10T00:00:00Z', bucket_end: '2026-04-11T00:00:00Z', total_items: 10, active_items: 7, delivered_stopped_items: 2, stale_items: 1 },
            { bucket: 'day', bucket_start: '2026-04-11T00:00:00Z', bucket_end: '2026-04-12T00:00:00Z', total_items: 11, active_items: 8, delivered_stopped_items: 2, stale_items: 1 },
            { bucket: 'day', bucket_start: '2026-04-12T00:00:00Z', bucket_end: '2026-04-13T00:00:00Z', total_items: 12, active_items: 9, delivered_stopped_items: 2, stale_items: 1 },
          ],
        },
      ],
    }
  }

  async function getSummaryByPlatform(period = '30d'): Promise<AdminSummaryByPlatformResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(baseUrl, `/admin/reports/summary_by_platform?period=${encodeURIComponent(period)}`)

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin summary-by-platform API error: ${response.status}`)
    }

    return await response.json() as AdminSummaryByPlatformResponse
  }

  function getSummaryByPlatformDemo(): AdminSummaryByPlatformResponse {
    return {
      period: '30d',
      window_start: '2026-03-16T00:00:00Z',
      window_end: '2026-04-15T00:00:00Z',
      totals: {
        total_items: 176,
        active_items: 127,
        delivered_stopped_items: 33,
        stale_items: 16,
        active_users: 90,
        average_items_tracked_per_active_user: 1.41,
      },
      platforms: [
        {
          platform: 'android',
          total_items: 110,
          active_items: 80,
          delivered_stopped_items: 20,
          stale_items: 10,
          active_users: 50,
          average_items_tracked_per_active_user: 1.6,
        },
        {
          platform: 'zeppos',
          total_items: 60,
          active_items: 44,
          delivered_stopped_items: 11,
          stale_items: 5,
          active_users: 35,
          average_items_tracked_per_active_user: 1.26,
        },
        {
          platform: 'unknown',
          total_items: 6,
          active_items: 3,
          delivered_stopped_items: 2,
          stale_items: 1,
          active_users: 5,
          average_items_tracked_per_active_user: 0.6,
        },
      ],
    }
  }

  async function getSeriesByPlatform(period = '30d', bucket = 'day'): Promise<AdminSeriesByPlatformResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(
      baseUrl,
      `/admin/reports/series_by_platform?period=${encodeURIComponent(period)}&bucket=${encodeURIComponent(bucket)}`,
    )

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin series-by-platform API error: ${response.status}`)
    }

    return await response.json() as AdminSeriesByPlatformResponse
  }

  function getSeriesByPlatformDemo(): AdminSeriesByPlatformResponse {
    return {
      period: '30d',
      bucket: 'day',
      window_start: '2026-03-16T00:00:00Z',
      window_end: '2026-04-15T00:00:00Z',
      totals: {
        total_items: 32,
        active_items: 25,
        delivered_stopped_items: 5,
        stale_items: 2,
        active_users: 20,
        average_items_tracked_per_active_user: 1.25,
      },
      platforms: [
        {
          platform: 'android',
          points: [
            { bucket: 'day', bucket_start: '2026-04-11T00:00:00Z', bucket_end: '2026-04-12T00:00:00Z', total_items: 28, active_items: 20, delivered_stopped_items: 5, stale_items: 3, active_users: 18, average_items_tracked_per_active_user: 1.11 },
            { bucket: 'day', bucket_start: '2026-04-12T00:00:00Z', bucket_end: '2026-04-13T00:00:00Z', total_items: 30, active_items: 22, delivered_stopped_items: 5, stale_items: 3, active_users: 19, average_items_tracked_per_active_user: 1.16 },
            { bucket: 'day', bucket_start: '2026-04-13T00:00:00Z', bucket_end: '2026-04-14T00:00:00Z', total_items: 31, active_items: 23, delivered_stopped_items: 5, stale_items: 3, active_users: 20, average_items_tracked_per_active_user: 1.15 },
            { bucket: 'day', bucket_start: '2026-04-14T00:00:00Z', bucket_end: '2026-04-15T00:00:00Z', total_items: 32, active_items: 25, delivered_stopped_items: 5, stale_items: 2, active_users: 20, average_items_tracked_per_active_user: 1.25 },
          ],
        },
        {
          platform: 'zeppos',
          points: [
            { bucket: 'day', bucket_start: '2026-04-11T00:00:00Z', bucket_end: '2026-04-12T00:00:00Z', total_items: 19, active_items: 13, delivered_stopped_items: 4, stale_items: 2, active_users: 12, average_items_tracked_per_active_user: 1.08 },
            { bucket: 'day', bucket_start: '2026-04-12T00:00:00Z', bucket_end: '2026-04-13T00:00:00Z', total_items: 22, active_items: 15, delivered_stopped_items: 4, stale_items: 3, active_users: 13, average_items_tracked_per_active_user: 1.15 },
            { bucket: 'day', bucket_start: '2026-04-13T00:00:00Z', bucket_end: '2026-04-14T00:00:00Z', total_items: 23, active_items: 16, delivered_stopped_items: 4, stale_items: 3, active_users: 15, average_items_tracked_per_active_user: 1.07 },
            { bucket: 'day', bucket_start: '2026-04-14T00:00:00Z', bucket_end: '2026-04-15T00:00:00Z', total_items: 26, active_items: 18, delivered_stopped_items: 5, stale_items: 3, active_users: 17, average_items_tracked_per_active_user: 1.06 },
          ],
        },
        {
          platform: 'unknown',
          points: [
            { bucket: 'day', bucket_start: '2026-04-14T00:00:00Z', bucket_end: '2026-04-15T00:00:00Z', total_items: 6, active_items: 3, delivered_stopped_items: 2, stale_items: 1, active_users: 5, average_items_tracked_per_active_user: 0.6 },
          ],
        },
      ],
    }
  }

  async function getNotificationsReport(period = '30d'): Promise<AdminNotificationsReportResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(baseUrl, `/admin/reports/notifications?period=${encodeURIComponent(period)}`)

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin notifications report API error: ${response.status}`)
    }

    return await response.json() as AdminNotificationsReportResponse
  }

  function getNotificationsReportDemo(): AdminNotificationsReportResponse {
    return {
      period: '30d',
      window_start: '2026-03-16T20:03:47.615007Z',
      window_end: '2026-04-15T20:03:47.615007Z',
      total: 38,
      sent: 35,
      failed: 3,
      suppressed: 0,
      pending: 0,
      by_type: [
        {
          notification_type: 'popup',
          total: 5,
          sent: 4,
          failed: 1,
          suppressed: 0,
          pending: 0,
        },
        {
          notification_type: 'silent',
          total: 33,
          sent: 31,
          failed: 2,
          suppressed: 0,
          pending: 0,
        },
      ],
      top_failure_reasons: [
        {
          reason: 'no_device',
          count: 3,
        },
      ],
    }
  }

  async function getNotificationIntegrityReport(period = '30d'): Promise<AdminNotificationIntegrityReportResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(baseUrl, `/admin/reports/notification_integrity?period=${encodeURIComponent(period)}`)

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin notification integrity report API error: ${response.status}`)
    }

    return await response.json() as AdminNotificationIntegrityReportResponse
  }

  function getNotificationIntegrityReportDemo(): AdminNotificationIntegrityReportResponse {
    return {
      period: '30d',
      window_start: '2026-03-16T20:03:47.615007Z',
      window_end: '2026-04-15T20:03:47.615007Z',
      total_rows: 112,
      valid_rows: 95,
      invalid_rows: 17,
      invalid_carrier_rows: 6,
      invalid_fcm_token_rows: 8,
      failed_invalid_rows: 3,
      invalid_by_status: [
        {
          delivery_status: 'failed_invalid_fcm_token',
          count: 8,
        },
        {
          delivery_status: 'failed_invalid_carrier',
          count: 6,
        },
        {
          delivery_status: 'failed_invalid_payload',
          count: 3,
        },
      ],
    }
  }

  async function getBrandfetchMetrics(period = '30d'): Promise<AdminBrandfetchMetricsResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(baseUrl, `/admin/brandfetch_metrics?period=${encodeURIComponent(period)}`)

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin brandfetch metrics API error: ${response.status}`)
    }

    return await response.json() as AdminBrandfetchMetricsResponse
  }

  function getBrandfetchMetricsDemo(): AdminBrandfetchMetricsResponse {
    return {
      metrics: {
        brandfetch_calls_total: 0,
        brandfetch_brand_calls_total: 0,
        brandfetch_search_calls_total: 0,
        brandfetch_cache_hits_total: 0,
        brandfetch_updates_total: 0,
        cache_hit_rate: 0,
        update_success_rate: 0,
        total_carriers: 3259,
        carriers_with_colors: 66,
        carriers_missing_colors: 3193,
        coverage_rate: 0.020251610923596196,
        domain_mappings_total: 295,
        domain_mapped_with_colors: 64,
        domain_mapped_missing_colors: 184,
        domain_mapped_coverage_rate: 0.21694915254237288,
        last_color_fetched_at: '2026-04-15T15:43:45.750232-04:00',
        brand_api_calls_current_month: 0,
        brand_api_limit_monthly: 500,
        brand_api_remaining_monthly: 500,
        brand_api_percent_used: 0,
        search_api_calls_current_month: 0,
        search_api_limit_monthly: 500,
        search_api_remaining_monthly: 500,
        search_api_percent_used: 0,
      },
    }
  }

  async function getBrandfetchReport(period = '30d'): Promise<AdminBrandfetchReportResponse> {
    const configuredBase = import.meta.env.VITE_ADMIN_REPORTS_BASE_URL ?? ''
    const baseUrl = resolveApiBase(configuredBase, '/api/v1')
    const endpoint = joinApiUrl(baseUrl, `/admin/reports/brandfetch?period=${encodeURIComponent(period)}`)

    if (!baseUrl) {
      throw new Error('Missing VITE_ADMIN_REPORTS_BASE_URL')
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Admin brandfetch report API error: ${response.status}`)
    }

    return await response.json() as AdminBrandfetchReportResponse
  }

  function getBrandfetchReportDemo(): AdminBrandfetchReportResponse {
    return {
      total_carriers: 3259,
      carriers_with_colors: 66,
      carriers_missing_colors: 3193,
      domain_mappings_total: 295,
      domain_mapped_with_colors: 64,
      domain_mapped_missing_colors: 184,
      domain_mapped_missing: [
        '11000Miles',
        '218 Logistics',
        '247Express',
      ],
      colored_carriers: [
        {
          carrier_code: 190135,
          carrier_name: '139Express',
          carrier_bg: '#333333',
          carrier_txt: '#b81b22',
          color_fetched_at: '2026-04-15T15:43:45.750232-04:00',
        },
        {
          carrier_code: 100003,
          carrier_name: 'FedEx',
          carrier_bg: '#4D148C',
          carrier_txt: '#FF6600',
          color_fetched_at: null,
        },
        {
          carrier_code: 100002,
          carrier_name: 'UPS',
          carrier_bg: '#301506',
          carrier_txt: '#FFB500',
          color_fetched_at: null,
        },
      ],
      brandfetch_calls_total: 0,
      brandfetch_cache_hits_total: 0,
      brandfetch_updates_total: 0,
    }
  }

  return {
    user,
    expiresAt,
    authError,
    hasCheckedSession,
    selectedPeriod,
    selectedBucket,
    isAuthenticated,
    login,
    ensureSession,
    logout,
    isLoggedIn,
    getUsageSummary,
    getUsageSummaryDemo,
    getDashboardReport,
    getDashboardReportDemo,
    getUsageSeries,
    getUsageSeriesDemo,
    getItemsPerCarrier,
    getItemsPerCarrierDemo,
    getSeriesPerCarrier,
    getSeriesPerCarrierDemo,
    getSummaryByPlatform,
    getSummaryByPlatformDemo,
    getSeriesByPlatform,
    getSeriesByPlatformDemo,
    getNotificationsReport,
    getNotificationsReportDemo,
    getNotificationIntegrityReport,
    getNotificationIntegrityReportDemo,
    getBrandfetchMetrics,
    getBrandfetchMetricsDemo,
    getBrandfetchReport,
    getBrandfetchReportDemo,
  }
})
